import { useState } from 'react'
import { useParams } from 'react-router'
import { CircleAlert, Lock, MessageSquare, ShieldAlert } from 'lucide-react'
import {
  DateValue, EmptyState, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import {
  useHelpdeskMutations, useTicket, useTicketComments, useTicketEscalations,
} from '../api'
import { TICKET_PRIORITY_LABELS, TICKET_STATUS_LABELS } from '../types'
import type { TicketPriority, TicketStatus } from '../types'

const STATUS_TONE: Record<TicketStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  NEW: 'info',
  ASSIGNED: 'info',
  IN_PROGRESS: 'warning',
  WAITING: 'neutral',
  RESOLVED: 'success',
  CLOSED: 'neutral',
  CANCELLED: 'neutral',
}

/** One ticket: the conversation, the clock, and what happened when it ran out. */
export function TicketDetailPage() {
  useBreadcrumb()
  const { id } = useParams<{ id: string }>()
  const ticket = useTicket(id)
  const comments = useTicketComments(id)
  const escalations = useTicketEscalations(id)
  const { comment, resolve, reopen, close, rate } = useHelpdeskMutations()

  const [reply, setReply] = useState('')
  const [internal, setInternal] = useState(false)
  const [resolveDraft, setResolveDraft] = useState<string | null>(null)
  const [reopenDraft, setReopenDraft] = useState<string | null>(null)
  const [rateDraft, setRateDraft] = useState<{ score: string; comment: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dialogError, setDialogError] = useState<string | null>(null)

  const act = (work: () => Promise<unknown>, fallback: string, inDialog = false) => {
    void (async () => {
      setError(null)
      setDialogError(null)
      try {
        await work()
      } catch (caught) {
        const message = toApiProblem(caught)?.detail ?? fallback
        if (inDialog) setDialogError(message)
        else setError(message)
      }
    })()
  }

  if (ticket.isPending) {
    return <Skeleton className="h-64" />
  }
  if (!ticket.isSuccess) {
    return (
      <EmptyState
        title="That ticket could not be opened"
        description="It may not exist, or it may be confidential and not yours to read."
      />
    )
  }

  const data = ticket.data

  return (
    <div className="space-y-4">
      <PageHeader
        title={`${data.ticketNo} · ${data.subject}`}
        subtitle={[
          data.categoryName,
          TICKET_PRIORITY_LABELS[data.priority as TicketPriority],
          data.raisedByName ? `raised by ${data.raisedByName}` : null,
        ].filter(Boolean).join(' · ')}
        actions={
          <div className="flex flex-wrap gap-2">
            {data.status !== 'RESOLVED' && data.status !== 'CLOSED' && (
              <Button type="button" onClick={() => { setError(null); setResolveDraft('') }}>
                Resolve
              </Button>
            )}
            {(data.status === 'RESOLVED' || data.status === 'CLOSED') && (
              <Button
                type="button" variant="outline"
                onClick={() => { setError(null); setReopenDraft('') }}
              >
                Reopen
              </Button>
            )}
            {data.status === 'RESOLVED' && (
              <>
                <Button
                  type="button" variant="outline"
                  onClick={() => act(() => close.mutateAsync(data.id), 'That could not be closed')}
                >
                  Close
                </Button>
                {data.satisfactionScore == null && (
                  <Button
                    type="button" variant="outline"
                    onClick={() => { setError(null); setRateDraft({ score: '5', comment: '' }) }}
                  >
                    Rate the answer
                  </Button>
                )}
              </>
            )}
          </div>
        }
      />

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      {data.confidentialityNote && (
        <p className="flex items-start gap-2 rounded-md border border-slate-300 bg-slate-100 p-3 text-sm text-[var(--color-heading)]">
          <Lock className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          {data.confidentialityNote}
        </p>
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        <Card className="space-y-2 p-4 text-sm">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-[var(--color-heading)]">Where it stands</h2>
            <StatusBadge
              status={data.status}
              tone={STATUS_TONE[data.status as TicketStatus] ?? 'neutral'}
              label={TICKET_STATUS_LABELS[data.status as TicketStatus] ?? data.status}
            />
          </div>
          <p className="text-slate-600">
            {data.assigneeName ? `With ${data.assigneeName}.` : 'Nobody is on it yet.'}
            {data.reopenedCount > 0 && ` Reopened ${data.reopenedCount} time(s).`}
          </p>
          {data.description && <p className="text-slate-700">{data.description}</p>}
          {data.resolution && (
            <p className="rounded-md bg-emerald-50 p-2 text-emerald-900">
              Resolved: {data.resolution}
            </p>
          )}
          {data.satisfactionScore != null && (
            <p className="text-slate-600">Rated {data.satisfactionScore} of 5.</p>
          )}
        </Card>

        <Card className="space-y-2 p-4 text-sm">
          <h2 className="font-semibold text-[var(--color-heading)]">What was promised</h2>
          <p className={`flex items-start gap-2 ${
            data.responseBreached || data.resolutionBreached
              ? 'text-amber-800' : 'text-slate-600'
          }`}>
            {(data.responseBreached || data.resolutionBreached) && (
              <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            )}
            {data.slaNote}
          </p>
          {escalations.isSuccess && escalations.data.length > 0 && (
            <div className="space-y-1 border-t border-slate-100 pt-2">
              <h3 className="flex items-center gap-2 font-medium text-[var(--color-heading)]">
                <ShieldAlert className="size-4" aria-hidden="true" />
                Escalations
              </h3>
              {escalations.data.map((row) => (
                <p key={row.id} className="text-xs text-slate-600">
                  <DateValue value={row.escalatedAt} /> · {row.breached.toLowerCase()} · {row.note}
                </p>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card>
        <h2 className="flex items-center gap-2 border-b border-slate-100 p-4 font-semibold text-[var(--color-heading)]">
          <MessageSquare className="size-4" aria-hidden="true" />
          Conversation
        </h2>
        {comments.isSuccess && comments.data.length === 0 && (
          <EmptyState
            title="Nobody has said anything yet"
            description="The first public reply stops the response clock. An internal note does not — nobody waiting has heard it."
          />
        )}
        {comments.isSuccess && comments.data.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {comments.data.map((row) => (
              <li key={row.id} className={`p-4 text-sm ${row.internal ? 'bg-amber-50' : ''}`}>
                <p className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                  <span className="font-medium text-[var(--color-heading)]">{row.authorName ?? 'Somebody'}</span>
                  <DateValue value={row.createdAt} />
                  {row.internal && (
                    <span className="rounded bg-amber-200 px-1.5 py-0.5 text-amber-900">
                      Internal note — the requester does not see this
                    </span>
                  )}
                </p>
                <p className="mt-1 whitespace-pre-wrap text-[var(--color-heading)]">{row.body}</p>
              </li>
            ))}
          </ul>
        )}

        <div className="space-y-2 border-t border-slate-100 p-4">
          <Label htmlFor="ticket-reply">Reply</Label>
          <Input
            id="ticket-reply" value={reply}
            onChange={(event) => setReply(event.target.value)}
          />
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                id="ticket-internal" type="checkbox" className="size-4"
                checked={internal}
                onChange={(event) => setInternal(event.target.checked)}
              />
              Internal note — does not stop the response clock
            </label>
            <Button
              type="button"
              disabled={!reply.trim()}
              onClick={() => act(async () => {
                await comment.mutateAsync({ id: data.id, body: reply, internal })
                setReply('')
              }, 'That comment could not be added')}
            >
              Post
            </Button>
          </div>
        </div>
      </Card>

      <FormDialog
        open={resolveDraft != null}
        onOpenChange={(open) => { if (!open) { setResolveDraft(null); setDialogError(null) } }}
        title="Resolve this ticket"
        description="Say what was actually done. A year from now this is the whole record."
        submitLabel="Resolve"
        loading={resolve.isPending}
        error={dialogError}
        onSubmit={() => {
          if (resolveDraft == null) return
          act(async () => {
            await resolve.mutateAsync({ id: data.id, resolution: resolveDraft })
            setResolveDraft(null)
          }, 'That ticket could not be resolved', true)
        }}
      >
        {resolveDraft != null && (
          <div>
            <Label htmlFor="resolve-text">What was done</Label>
            <Input
              id="resolve-text" value={resolveDraft}
              onChange={(event) => setResolveDraft(event.target.value)}
            />
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={reopenDraft != null}
        onOpenChange={(open) => { if (!open) { setReopenDraft(null); setDialogError(null) } }}
        title="Reopen this ticket"
        description="Reopening is counted: a queue where a third of the answers come back is a different queue."
        submitLabel="Reopen"
        loading={reopen.isPending}
        error={dialogError}
        onSubmit={() => {
          if (reopenDraft == null) return
          act(async () => {
            await reopen.mutateAsync({ id: data.id, reason: reopenDraft })
            setReopenDraft(null)
          }, 'That ticket could not be reopened', true)
        }}
      >
        {reopenDraft != null && (
          <div>
            <Label htmlFor="reopen-reason">Why it is back</Label>
            <Input
              id="reopen-reason" value={reopenDraft}
              onChange={(event) => setReopenDraft(event.target.value)}
            />
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={rateDraft != null}
        onOpenChange={(open) => { if (!open) { setRateDraft(null); setDialogError(null) } }}
        title="Rate the answer"
        description="Once per ticket: a second score would replace somebody's answer with a later one."
        submitLabel="Send"
        loading={rate.isPending}
        error={dialogError}
        onSubmit={() => {
          if (!rateDraft) return
          act(async () => {
            await rate.mutateAsync({
              id: data.id, score: Number(rateDraft.score),
              comment: rateDraft.comment || null,
            })
            setRateDraft(null)
          }, 'That rating could not be recorded', true)
        }}
      >
        {rateDraft && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="rate-score">Score</Label>
              <Select
                id="rate-score"
                className="w-full"
                value={rateDraft.score}
                onChange={(event) => setRateDraft({ ...rateDraft, score: event.target.value })}
              >
                {[1, 2, 3, 4, 5].map((score) => (
                  <option key={score} value={String(score)}>{score} of 5</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="rate-comment">Anything to add</Label>
              <Input
                id="rate-comment" value={rateDraft.comment}
                onChange={(event) => setRateDraft({ ...rateDraft, comment: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
